import type { CardTheme } from "./types";

/**
 * The result of resolving a theme to concrete style values.
 * Each card component reads these instead of hardcoding the brand look,
 * which lets one switch flip the entire visual treatment of the card.
 */
export interface ResolvedTheme {
  /** Pass to FbkCardShell.customBaseGradient to swap the brand gradient. */
  customBaseGradient?: string;
  /** Suppresses the gold sunrise + center halo + white grid overlays. */
  suppressBrandOverlays: boolean;
  /** Flip header text/pill to dark variants for light backgrounds. */
  headerOnLight: boolean;
  /** Optional decorative overlay rendered above the shell, below content. */
  decorativeOverlay?:
    | "spotlight-halo"
    | "halftone-dots"
    | "editorial-rules"
    | "knockout-brackets"
    | "trophy-sparkle"
    | "anthem-rays"
    | "emerald-noir-ring";

  // Type defaults — each card can still override per-field via CardStyleSettings
  fontHeadline: string;
  fontBody: string;
  fontEyebrow: string;

  // Color defaults
  headlineColor: string;
  /** Accent / highlight color — usually gold, but theme-aware. */
  accentColor: string;
  /** Body text + secondary info color. */
  subtleTextColor: string;
  /** Decorative ring/border color for images. */
  ringColor: string;
  /** Pull-quote vertical bar color. */
  quoteBarColor: string;
  /** CTA pill background. */
  ctaBg: string;
  /** CTA pill text color. */
  ctaFg: string;
}

/** Curated font stacks reused across themes. */
const FONT_BEBAS = "'Bebas Neue', 'Anton', sans-serif";
const FONT_INTER = "'Inter', system-ui, sans-serif";
const FONT_PLAYFAIR = "'Playfair Display', Georgia, serif";

const EDITORIAL_BG = "#F4EDD9";
const EDITORIAL_INK = "#1A2E1F";
const EDITORIAL_ACCENT = "#A02928";

/** Rough relative luminance for a hex color. 0 = dark, 1 = light. */
export function luminance(hex: string): number {
  const c = hex.replace("#", "").trim();
  if (c.length !== 6) return 0;
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export interface ResolveCtx {
  /** Brand-green default unless the card supplies its own monochrome color. */
  monoColor?: string;
  /** A secondary brand color used by the "aurora" theme alongside gold. */
  auroraSecondary?: string;
  /** Partner brand color used by the "collab" theme for the diagonal split. */
  partnerColor?: string;
}

/**
 * Map a theme ID + context into concrete CSS-ready style values.
 * Used by every card component to drive its visual treatment.
 */
export function resolveCardTheme(
  themeId: CardTheme,
  ctx: ResolveCtx = {},
): ResolvedTheme {
  const monoColor = ctx.monoColor || "#0D8C3C";
  const monoBgIsLight = luminance(monoColor) > 0.55;
  const auroraSecondary = ctx.auroraSecondary || "#0D5C2E";

  switch (themeId) {
    case "spotlight":
      return {
        customBaseGradient:
          "radial-gradient(ellipse 70% 60% at 50% 48%, #1c3a25 0%, #0b1f12 50%, #000000 100%)",
        suppressBrandOverlays: false,
        headerOnLight: false,
        decorativeOverlay: "spotlight-halo",
        fontHeadline: FONT_BEBAS,
        fontBody: FONT_INTER,
        fontEyebrow: FONT_INTER,
        headlineColor: "#FFFFFF",
        accentColor: "#FFD700",
        subtleTextColor: "rgba(255,255,255,0.85)",
        ringColor: "#FFD700",
        quoteBarColor: "#FFD700",
        ctaBg: "#FFD700",
        ctaFg: "#0A4724",
      };

    case "aurora":
      return {
        customBaseGradient: [
          `radial-gradient(ellipse at 15% 20%, ${monoColor} 0%, transparent 55%)`,
          `radial-gradient(ellipse at 85% 80%, ${auroraSecondary} 0%, transparent 55%)`,
          "radial-gradient(ellipse at 50% 50%, rgba(255,215,0,0.35) 0%, transparent 60%)",
          "linear-gradient(135deg, #0D5C2E 0%, #07321A 100%)",
        ].join(", "),
        suppressBrandOverlays: false,
        headerOnLight: false,
        fontHeadline: FONT_BEBAS,
        fontBody: FONT_INTER,
        fontEyebrow: FONT_INTER,
        headlineColor: "#FFFFFF",
        accentColor: "#FFD700",
        subtleTextColor: "rgba(255,255,255,0.85)",
        ringColor: "#FFD700",
        quoteBarColor: "#FFD700",
        ctaBg: "#FFD700",
        ctaFg: "#0A4724",
      };

    case "editorial":
      return {
        customBaseGradient: EDITORIAL_BG,
        suppressBrandOverlays: true,
        headerOnLight: true,
        decorativeOverlay: "editorial-rules",
        fontHeadline: FONT_PLAYFAIR,
        fontBody: FONT_INTER,
        fontEyebrow: FONT_INTER,
        headlineColor: EDITORIAL_INK,
        accentColor: EDITORIAL_ACCENT,
        subtleTextColor: "rgba(26,46,31,0.75)",
        ringColor: EDITORIAL_ACCENT,
        quoteBarColor: EDITORIAL_ACCENT,
        ctaBg: EDITORIAL_INK,
        ctaFg: EDITORIAL_BG,
      };

    case "halftone":
      // Solid base from monoColor; dot pattern is overlay
      return {
        customBaseGradient: monoColor,
        suppressBrandOverlays: true,
        headerOnLight: monoBgIsLight,
        decorativeOverlay: "halftone-dots",
        fontHeadline: FONT_BEBAS,
        fontBody: FONT_INTER,
        fontEyebrow: FONT_INTER,
        headlineColor: monoBgIsLight ? "#1A1A1A" : "#FFFFFF",
        accentColor: monoBgIsLight ? "#A02928" : "#FFD700",
        subtleTextColor: monoBgIsLight ? "rgba(0,0,0,0.75)" : "rgba(255,255,255,0.85)",
        ringColor: monoBgIsLight ? "#1A1A1A" : "#FFD700",
        quoteBarColor: monoBgIsLight ? "#1A1A1A" : "#FFD700",
        ctaBg: monoBgIsLight ? "#1A1A1A" : "#FFD700",
        ctaFg: monoBgIsLight ? "#FFFFFF" : "#0A4724",
      };

    case "monochrome": {
      const monoTextPrimary = monoBgIsLight ? "#07321A" : "#FFFFFF";
      const monoTextAccent = monoBgIsLight ? "#0D5C2E" : "#FFD700";
      return {
        customBaseGradient: monoColor,
        suppressBrandOverlays: true,
        headerOnLight: monoBgIsLight,
        fontHeadline: FONT_BEBAS,
        fontBody: FONT_INTER,
        fontEyebrow: FONT_INTER,
        headlineColor: monoTextPrimary,
        accentColor: monoTextAccent,
        subtleTextColor: monoBgIsLight ? "rgba(0,0,0,0.7)" : "rgba(255,255,255,0.85)",
        ringColor: monoTextAccent,
        quoteBarColor: monoTextAccent,
        ctaBg: monoTextAccent,
        ctaFg: monoBgIsLight ? "#FFFFFF" : "#0A4724",
      };
    }

    case "collab": {
      // Diagonal split: FBK brand green on the left, partner color on the right.
      // Defaults partner-side to the brand green if no partnerColor is set —
      // gives a graceful "all green" look until the admin picks a partner color.
      const partnerColor = ctx.partnerColor || "#0D5C2E";
      const partnerIsLight = luminance(partnerColor) > 0.55;
      return {
        customBaseGradient: [
          `linear-gradient(115deg, #0D8C3C 0%, #0D5C2E 48%, ${partnerColor} 52%, ${partnerColor} 100%)`,
        ].join(", "),
        suppressBrandOverlays: true,
        headerOnLight: false,
        fontHeadline: FONT_BEBAS,
        fontBody: FONT_INTER,
        fontEyebrow: FONT_INTER,
        headlineColor: "#FFFFFF",
        accentColor: "#FFD700",
        subtleTextColor: "rgba(255,255,255,0.92)",
        ringColor: partnerIsLight ? "#1A1A1A" : "#FFD700",
        quoteBarColor: "#FFD700",
        ctaBg: "#FFD700",
        ctaFg: "#0A4724",
      };
    }

    case "knockout":
      // Deep emerald saturation with subtle gold tournament-bracket lines.
      // Reads as "high-stakes elimination round." The bracket pattern is
      // SVG-driven in ThemeOverlay so it scales with format.
      return {
        customBaseGradient: [
          "radial-gradient(ellipse 80% 50% at 50% 30%, rgba(255,215,0,0.18) 0%, transparent 60%)",
          "linear-gradient(180deg, #07321A 0%, #0D5C2E 45%, #07321A 100%)",
        ].join(", "),
        suppressBrandOverlays: true,
        headerOnLight: false,
        decorativeOverlay: "knockout-brackets",
        fontHeadline: FONT_BEBAS,
        fontBody: FONT_INTER,
        fontEyebrow: FONT_INTER,
        headlineColor: "#FFFFFF",
        accentColor: "#FFD700",
        subtleTextColor: "rgba(255,255,255,0.88)",
        ringColor: "#FFD700",
        quoteBarColor: "#FFD700",
        ctaBg: "#FFD700",
        ctaFg: "#07321A",
      };

    case "trophy":
      // Black base + huge bottom-up gold halo + corner sparkle. Reads as
      // a glittering trophy lit on a dark plinth — "championship" energy.
      return {
        customBaseGradient: [
          "radial-gradient(ellipse 95% 60% at 50% 100%, rgba(255,215,0,0.42) 0%, rgba(255,165,0,0.12) 30%, transparent 65%)",
          "radial-gradient(ellipse 40% 30% at 50% 50%, rgba(13,140,60,0.18) 0%, transparent 70%)",
          "linear-gradient(180deg, #000000 0%, #0A0F08 60%, #050805 100%)",
        ].join(", "),
        suppressBrandOverlays: true,
        headerOnLight: false,
        decorativeOverlay: "trophy-sparkle",
        fontHeadline: FONT_BEBAS,
        fontBody: FONT_INTER,
        fontEyebrow: FONT_INTER,
        headlineColor: "#FFFFFF",
        accentColor: "#FFE17A", // warmer gold under the halo
        subtleTextColor: "rgba(255,232,180,0.85)",
        ringColor: "#FFD700",
        quoteBarColor: "#FFD700",
        ctaBg: "#FFD700",
        ctaFg: "#0A0A0A",
      };

    case "anthem":
      // Vertical flag-band layout — emerald with thin gold ribbons left
      // and right of a slightly lighter central band. Built into the base
      // gradient so it ALWAYS reads as bands, even past the shell padding.
      return {
        customBaseGradient: [
          "linear-gradient(90deg," +
          " #07321A 0%, #07321A 6%," +
          " #FFD700 6%, #FFD700 7.2%," + // left gold ribbon
          " #0A4724 7.2%, #0D5C2E 50%, #0A4724 92.8%," + // center field
          " #FFD700 92.8%, #FFD700 94%," + // right gold ribbon
          " #07321A 94%, #07321A 100%)",
        ].join(", "),
        suppressBrandOverlays: true,
        headerOnLight: false,
        decorativeOverlay: "anthem-rays",
        fontHeadline: FONT_BEBAS,
        fontBody: FONT_INTER,
        fontEyebrow: FONT_INTER,
        headlineColor: "#FFFFFF",
        accentColor: "#FFD700",
        subtleTextColor: "rgba(255,255,255,0.92)",
        ringColor: "#FFD700",
        quoteBarColor: "#FFD700",
        ctaBg: "#FFD700",
        ctaFg: "#07321A",
      };

    case "emerald-noir":
      // Black base + tight emerald radial pulse behind the headline. The
      // gold seam ring is drawn by the overlay, not the gradient — it
      // hugs a centered card-shape so the type sits inside a halo of
      // brand light. Broadcast-open feel.
      return {
        customBaseGradient: [
          "radial-gradient(ellipse 60% 45% at 50% 50%, #0D5C2E 0%, #07321A 35%, #000000 75%)",
          "linear-gradient(180deg, #000000 0%, #050505 100%)",
        ].join(", "),
        suppressBrandOverlays: true,
        headerOnLight: false,
        decorativeOverlay: "emerald-noir-ring",
        fontHeadline: FONT_BEBAS,
        fontBody: FONT_INTER,
        fontEyebrow: FONT_INTER,
        headlineColor: "#FFFFFF",
        accentColor: "#FFD700",
        subtleTextColor: "rgba(255,255,255,0.82)",
        ringColor: "#FFD700",
        quoteBarColor: "#FFD700",
        ctaBg: "#FFD700",
        ctaFg: "#07321A",
      };

    case "signature":
    default:
      return {
        customBaseGradient: undefined, // uses FbkCardShell default brand gradient
        suppressBrandOverlays: false,
        headerOnLight: false,
        fontHeadline: FONT_BEBAS,
        fontBody: FONT_INTER,
        fontEyebrow: FONT_INTER,
        headlineColor: "#FFFFFF",
        accentColor: "#FFD700",
        subtleTextColor: "rgba(255,255,255,0.85)",
        ringColor: "#FFD700",
        quoteBarColor: "#FFD700",
        ctaBg: "#FFD700",
        ctaFg: "#0A4724",
      };
  }
}

/** Human-friendly labels + descriptions for the theme picker UI. */
export const THEME_META: Record<
  CardTheme,
  { label: string; desc: string }
> = {
  signature: {
    label: "FBK signature",
    desc: "Brand green gradient + gold glow. The default.",
  },
  spotlight: {
    label: "Spotlight",
    desc: "Dark stadium night + gold halo. Big-moment energy.",
  },
  aurora: {
    label: "Aurora",
    desc: "Smooth color blend. Atmospheric and modern.",
  },
  editorial: {
    label: "Editorial",
    desc: "Cream paper + serif headlines. Magazine-cover feel.",
  },
  halftone: {
    label: "Halftone",
    desc: "Retro dot pattern + bold inks. Pop-art / Panini.",
  },
  monochrome: {
    label: "Monochrome",
    desc: "Full-bleed single color. Bold confident poster.",
  },
  collab: {
    label: "Collab split",
    desc: "Diagonal split — FBK green meets partner color. Co-brand feel.",
  },
  knockout: {
    label: "Knockout",
    desc: "Deep emerald + tournament-bracket lines. Round-of-32 drama.",
  },
  trophy: {
    label: "Trophy gleam",
    desc: "Black plinth + huge gold halo + corner sparkle. Championship energy.",
  },
  anthem: {
    label: "Anthem",
    desc: "Vertical flag bands — emerald + gold ribbons. Ceremonial moment.",
  },
  "emerald-noir": {
    label: "Emerald noir",
    desc: "Black + tight emerald pulse + gold seam ring. Broadcast open.",
  },
};

/** Theme-picker swatch (a CSS background value) for previewing a theme. */
export function themeSwatch(
  themeId: CardTheme,
  ctx: ResolveCtx = {},
): string {
  switch (themeId) {
    case "signature":
      return "linear-gradient(135deg, #0D8C3C 0%, #0D5C2E 60%, #07321A 100%)";
    case "spotlight":
      return "radial-gradient(ellipse 70% 60% at 50% 48%, #1c3a25 0%, #000000 100%)";
    case "aurora": {
      const monoColor = ctx.monoColor || "#0D8C3C";
      const aurora = ctx.auroraSecondary || "#0D5C2E";
      return [
        `radial-gradient(ellipse at 15% 20%, ${monoColor} 0%, transparent 55%)`,
        `radial-gradient(ellipse at 85% 80%, ${aurora} 0%, transparent 55%)`,
        "radial-gradient(ellipse at 50% 50%, rgba(255,215,0,0.4) 0%, transparent 60%)",
        "linear-gradient(135deg, #0D5C2E 0%, #07321A 100%)",
      ].join(", ");
    }
    case "editorial":
      return EDITORIAL_BG;
    case "halftone":
      return ctx.monoColor || "#0D8C3C";
    case "monochrome":
      return ctx.monoColor || "#0D8C3C";
    case "collab": {
      const partner = ctx.partnerColor || "#0D5C2E";
      return `linear-gradient(115deg, #0D8C3C 0%, #0D5C2E 48%, ${partner} 52%, ${partner} 100%)`;
    }
    case "knockout":
      return "linear-gradient(180deg, #07321A 0%, #0D5C2E 50%, #07321A 100%)";
    case "trophy":
      return "radial-gradient(ellipse at 50% 100%, #FFD700 0%, #1A0F00 60%, #000000 100%)";
    case "anthem":
      return (
        "linear-gradient(90deg," +
        " #07321A 0%, #07321A 6%," +
        " #FFD700 6%, #FFD700 7.2%," +
        " #0A4724 7.2%, #0D5C2E 50%, #0A4724 92.8%," +
        " #FFD700 92.8%, #FFD700 94%," +
        " #07321A 94%, #07321A 100%)"
      );
    case "emerald-noir":
      return "radial-gradient(ellipse at 50% 50%, #0D5C2E 0%, #07321A 35%, #000 75%)";
  }
}
